import {
  CustomIndicator,
  IBrokerConnectionAdapterHost,
  PineJS,
  SeriesType,
  StudyScript,
  TimeFrameType,
  TimeFrameValue,
  TradingTerminalWidgetOptions,
  widget,
} from '@frontends/feature-charting-library/charting_library';
import { ResolutionString, Timezone } from '@frontends/feature-charting-library/datafeed-api';
import {
  cTriggerIndicator,
  harsiIndicator,
  richGardenIndicator,
  sessionIndicator,
  usousdIndicator,
  utBotAlertsIndicator,
} from '@frontends/feature-indicators';
import {
  blockPageZoom,
  CHART_BRAND_COLOR,
  getChartThemeOverrides,
  LoadError,
  useChartLocale,
  useChartThemeSync,
  useGlobalLoading,
} from '@frontends/feature-tv-parts';
import { findLocale } from '@frontends/util-i18n';
import { useTheme } from '@frontends/util-theme-provider';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getParameterByName } from '../../app/utils/query';
import { Account } from '../../context/account';
import {
  initFacade,
  selectAccount,
  selectInitialLoaded,
  selectInitialLoadFailed,
  selectSymbols,
} from '../../features/initialSlice/initialSlice';
import {
  selectBalance,
  selectChartSpeed,
  selectChartType,
  selectCurrentDate,
  selectDateChangeCounter,
  selectIsFinishReached,
  selectIsPaused,
  selectIsReplayStarted,
  selectStepRes,
  setBalance,
  setChartRes,
  setChartType,
  setIsReplayStarted,
  setStepRes,
} from '../../features/mainSlice/mainSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { BrokerDemo } from './lib/broker/broker';
import { DataFeed } from './lib/datafeed';
import { ReplaySaveLoadAdapter } from './lib/ReplaySaveLoadAdapter';
import { calculateSessionStatistic } from './lib/statistics';
import { useChartLanguage } from './useChartLanguage';
import { widgetReadyTradingView } from './widgetDelegates/buttons';
import './tvChartContainer.css';

const ACCEPTABLE_CHART_TYPES = new Set<SeriesType>([0, 1, 2, 9, 14, 15, 19, 21]);

const MIN_BALANCE: Record<string, number> = {
  GBP: 10,
  EUR: 10,
  USD: 10,
  JPY: 1400,
  THB: 350,
};

function isBalanceTooLow(balance: number, currency: string): boolean {
  const min = MIN_BALANCE[currency.toUpperCase()];

  if (min === undefined) {
    return false;
  }

  return balance < min;
}

const LAST_SELECTED_SYMBOL_KEY = 'lastSelectedSymbol';
const DEFAULT_SYMBOL = 'eurusd';

function getInitialSymbol(symbols: { name: string }[]): string {
  const querySymbol = getParameterByName('symbol');

  if (querySymbol) {
    const match = symbols.find((symbol) => symbol.name.toLowerCase() === querySymbol.toLowerCase());

    if (match) return match.name;
  }

  const storedSymbol = localStorage.getItem(LAST_SELECTED_SYMBOL_KEY);

  if (storedSymbol) {
    const match = symbols.find((symbol) => symbol.name.toLowerCase() === storedSymbol.toLowerCase());

    if (match) {
      return match.name;
    }
  }

  const fallback = symbols.find((symbol) => symbol.name.toLowerCase().includes(DEFAULT_SYMBOL));

  if (fallback) {
    return fallback.name;
  }

  return symbols[0].name;
}

export const TradingViewWidget = (): React.JSX.Element => {
  const [datafeedAPI, setDataFeedAPI] = useState<DataFeed | null>(null);
  const dispatch = useAppDispatch();
  const symbols = useAppSelector(selectSymbols);
  const initialLoaded = useAppSelector(selectInitialLoaded);
  const isInitialLoadFailed = useAppSelector(selectInitialLoadFailed);
  const currentDate = useAppSelector(selectCurrentDate);
  const chartRes = useAppSelector(selectStepRes);
  const balance = useAppSelector(selectBalance);
  const account = useAppSelector(selectAccount);
  const isFinishReached = useAppSelector(selectIsFinishReached);
  const stateChartType = useAppSelector(selectChartType);
  const contextAccount = useContext(Account);
  const config = useEntityConfig();
  const { resolvedMode } = useTheme();
  const widgetRef = useRef<InstanceType<typeof widget> | null>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [readyWidget, setReadyWidget] = useState<InstanceType<typeof widget> | null>(null);
  const [isChartInitFailed, setIsChartInitFailed] = useState(false);
  const brokerRef = useRef<BrokerDemo | null>(null);
  const brokerHostRef = useRef<IBrokerConnectionAdapterHost | null>(null);
  const resolvedModeRef = useRef(resolvedMode);
  const balanceSetForLoginRef = useRef<string | number | null>(null);
  const stateChartTypeRef = useRef(stateChartType);
  const { i18n, t } = useTranslation();
  const isReplayStarted = useAppSelector(selectIsReplayStarted);
  const isPaused = useAppSelector(selectIsPaused);
  const { chartLanguage, savedLayoutRef } = useChartLanguage({
    appLanguage: findLocale(i18n.language).language,
    isSessionInProgress: isReplayStarted || isFinishReached || !isPaused,
    readyWidget,
  });
  const { translate, widgetLocale } = useChartLocale(chartLanguage);

  useEffect(() => {
    resolvedModeRef.current = resolvedMode;
  }, [resolvedMode]);

  useChartThemeSync(readyWidget, resolvedMode);

  const hasLoadError = isInitialLoadFailed || (initialLoaded && symbols.length === 0) || isChartInitFailed;

  useGlobalLoading(!isWidgetReady && !hasLoadError);

  useEffect(() => {
    stateChartTypeRef.current = stateChartType;
  }, [stateChartType]);

  useEffect(() => {
    if (!contextAccount.selectedClientAccountId) {
      return;
    }

    void dispatch(initFacade(contextAccount.selectedClientAccountId));
  }, [dispatch, contextAccount.selectedClientAccountId]);

  useEffect(() => {
    if (!account) return;

    if (balanceSetForLoginRef.current === account.login) return;

    if (isBalanceTooLow(account.balance, account.currency)) return;

    balanceSetForLoginRef.current = account.login;
    dispatch(setBalance(account.balance));
  }, [account, dispatch]);

  useEffect(() => {
    const datafeedAPIInstance = DataFeed.getInstance(config);
    setDataFeedAPI(datafeedAPIInstance);
  }, [config]);

  useLayoutEffect(() => {
    if (!datafeedAPI || !initialLoaded || symbols.length === 0) return;

    let cleanupTradingViewButton: (() => void) | null = null;
    const savedLayout = savedLayoutRef.current;

    const initializeWidget = async (): Promise<InstanceType<typeof widget> | null> => {
      setIsChartInitFailed(false);

      const isApplyEnabled = config.VITE_REPLAY_APPLY_SAVED_LAYOUT === true;
      const widgetTheme = resolvedModeRef.current;

      const login = contextAccount.selectedClientAccountId ?? 0;
      const saveLoadAdapter = await ReplaySaveLoadAdapter.initialize(login, widgetTheme, {
        bypass: config.VITE_ALL_SETTINGS_LOCAL === true,
        persistenceUrl: config.VITE_URL_PERSISTENCE_SERVICE,
        isApplyEnabled,
      });

      // Prefer the symbol from the most recent saved layout so it matches the layout
      // that load_last_chart will apply — otherwise the widget option overrides it
      // and the chart loads a different instrument than what was saved in tv-client.
      const savedCharts = await saveLoadAdapter.getAllCharts();

      if (!mounted) {
        return null;
      }

      const latestSavedChart = savedCharts
        .slice()
        .sort((left, right) => (right.timestamp ?? 0) - (left.timestamp ?? 0))[0];
      const savedSymbolName = latestSavedChart?.symbol;
      const initialSymbol =
        (savedSymbolName &&
          symbols.find((symbol) => symbol.name.toLowerCase() === savedSymbolName.toLowerCase())?.name) ||
        getInitialSymbol(symbols);

      const widgetOptions: TradingTerminalWidgetOptions = {
        debug: false,
        container: 'tv_chart_container',
        datafeed: datafeedAPI,
        interval: chartRes as ResolutionString,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone,
        ...(savedLayout ? { saved_data: savedLayout } : { symbol: initialSymbol }),
        custom_css_url: '/assets/styles/libraryStyles.css?v=3',
        library_path: '/charting_library/',
        locale: widgetLocale,
        autosize: true,
        load_last_chart: isApplyEnabled && !savedLayout,
        fullscreen: true,
        theme: widgetTheme,
        ...(isApplyEnabled ? { save_load_adapter: saveLoadAdapter } : {}),
        enabled_features: [
          'disable_resolution_rebuild',
          'request_only_visible_range_on_reset',
          ...(isApplyEnabled ? ['study_templates' as const] : []),
          'show_symbol_logos',
          'show_exchange_logos',
        ],
        custom_indicators_getter: (pineJS: PineJS): Promise<readonly (CustomIndicator | StudyScript)[]> =>
          Promise.resolve([
            utBotAlertsIndicator(pineJS, chartLanguage),
            harsiIndicator(pineJS, chartLanguage),
            sessionIndicator(pineJS, chartLanguage),
            richGardenIndicator(pineJS),
            cTriggerIndicator(pineJS),
            usousdIndicator(pineJS),
            // The builders spell `metainfo.id` as a plain string, while the vendor wants its
            // branded `RawStudyMetaInfoId`. The library erases the same friction behind `any`
            // in `IndicatorOrchestrator.getIndicators`; this keeps it to one line instead.
          ] as unknown as readonly (CustomIndicator | StudyScript)[]),
        disabled_features: [
          'trading_notifications',
          'header_layouttoggle',
          'support_multicharts',
          'chart_style_hilo',
          ...(isApplyEnabled ? [] : ['header_saveload' as const]),
        ],
        loading_screen: {
          foregroundColor: CHART_BRAND_COLOR,
        },
        widgetbar: {
          details: false,
          news: false,
          watchlist: false,
          datawindow: true,
          watchlist_settings: {
            default_symbols: [],
            readonly: false,
          },
        },
        custom_translate_function: translate,
        // @ts-expect-error BrokerDemo's placeOrder is looser than the vendor's IBrokerTerminal
        broker_factory: (host: IBrokerConnectionAdapterHost) => {
          console.log('LOADSING MANAGER', host);

          // @ts-expect-error the vendor declares IBrokerConnectionAdapterHost twice — in
          // charting_library and in broker-api — and BrokerDemo is written against the latter
          const brokerTemp = new BrokerDemo(host, datafeedAPI as DataFeed, contextAccount);
          console.log('BROKER', brokerTemp);

          brokerRef.current = brokerTemp;
          brokerHostRef.current = host;

          return brokerTemp;
        },
        // debug_broker: "broker-only",
        broker_config: {
          configFlags: {
            supportMultiposition: true,
            supportNativeReversePosition: true,
            supportClosePosition: true,
            supportPLUpdate: true,
            supportLevel2Data: false,
            showQuantityInsteadOfAmount: true,
            supportEditAmount: true,
            supportOrderBrackets: true,
            supportMarketBrackets: true,
            supportPositionBrackets: true,
            supportOrdersHistory: false,
            showNotificationsLog: false,
          },
          durations: [
            { name: 'GTC', value: 'GTC' },
            { name: 'DAY', value: 'DAY' },
          ],
        },
        overrides: {
          'scalesProperties.showBidAskLabels': true,
          'mainSeriesProperties.bidAsk.visible': true,
          'tradingProperties.showExecutions': true,
          ...getChartThemeOverrides(widgetTheme),
        },
      };

      const widgetInstance = new widget(widgetOptions);

      widgetInstance.onChartReady(() => {
        if (!mounted) return;

        const chartDocument = document.getElementById('tv_chart_container')?.querySelector('iframe')?.contentDocument;

        if (chartDocument) {
          blockPageZoom(chartDocument);
        }

        // load_last_chart applies the saved layout's interval BEFORE onChartReady fires, so
        // onIntervalChanged (subscribed below) never sees the initial value. Without this sync,
        // stepRes stays at the default ('240') while the actual chart resolution can be '1',
        // and the datafeed's filter window then never contains any of the step-grained bars.
        const initialInterval = widgetInstance.activeChart().resolution();
        dispatch(setChartRes(initialInterval));

        if (!savedLayout) {
          dispatch(setStepRes(initialInterval));
        }

        widgetInstance
          .activeChart()
          .onIntervalChanged()
          .subscribe(null, (interval, _timeframeObj) => {
            dispatch(setChartRes(interval));
            dispatch(setStepRes(interval));
          });
        widgetInstance
          .activeChart()
          .onChartTypeChanged()
          .subscribe(null, (chartType) => {
            if (ACCEPTABLE_CHART_TYPES.has(chartType)) {
              dispatch(setChartType(chartType));

              return;
            }

            widgetRef.current?.activeChart().setChartType(stateChartTypeRef.current);
            brokerHostRef.current?.showNotification(t('caution'), t('barReplayTypeNotAvailable'), 0);
          });

        widgetInstance
          .headerReady()
          .then(() => {
            if (!mounted) return;

            try {
              cleanupTradingViewButton = widgetReadyTradingView(widgetInstance, config) || null;
            } catch (error) {
              console.warn('widgetReadyTradingView failed', error);
            }
          })
          .catch((error: unknown) => {
            console.warn('headerReady error', error);
          });
        setIsWidgetReady(true);
        setReadyWidget(widgetInstance);
      });

      return widgetInstance;
    };

    let mounted = true;
    let widgetInstance: InstanceType<typeof widget> | null = null;
    initializeWidget()
      .then((instance) => {
        if (!mounted) {
          try {
            instance?.remove();
          } catch (error) {
            console.warn('widget remove error (unmounted)', error);
          }

          return;
        }

        widgetInstance = instance ?? null;
        widgetRef.current = widgetInstance;
      })
      .catch((error: unknown) => {
        if (!mounted) return;

        console.error('TradingView widget initialization failed:', error);
        setIsChartInitFailed(true);
      });

    return () => {
      mounted = false;
      setIsWidgetReady(false);
      setReadyWidget(null);
      const instance = widgetInstance ?? widgetRef.current;
      widgetRef.current = null;

      try {
        instance?.remove();
      } catch (error) {
        console.warn('widget remove error', error);
      }

      if (cleanupTradingViewButton) cleanupTradingViewButton();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    datafeedAPI,
    initialLoaded,
    symbols,
    contextAccount.selectedClientAccountId,
    dispatch,
    chartLanguage,
    translate,
    widgetLocale,
  ]);

  const dateChangeCounter = useSelector(selectDateChangeCounter);
  const stepResolution = useSelector(selectStepRes);
  const chartSpeed = useSelector(selectChartSpeed);
  useEffect(() => {
    if (isPaused) {
      datafeedAPI?.pauseTimer();
    } else {
      datafeedAPI?.startTimer();
    }
  }, [isPaused, datafeedAPI]);

  useEffect(() => {
    datafeedAPI?.changeFrequency(chartSpeed);
  }, [chartSpeed, datafeedAPI]);

  useEffect(() => {
    const broker = brokerRef.current;

    if (broker) {
      broker.setBalance(balance);
    }
  }, [balance, dateChangeCounter]);

  useEffect(() => {
    if (!isFinishReached) {
      return;
    }

    dispatch(setIsReplayStarted(false));
    const broker = brokerRef.current;

    if (broker) {
      broker
        .closeAllPositions()
        .then(() => {
          calculateSessionStatistic();
        })
        .catch((error: unknown) => {
          console.warn('closeAllPositions error', error);
        });
    }
  }, [isFinishReached, dispatch]);

  useEffect(() => {
    if (!isWidgetReady) {
      return;
    }

    const chartWidget = widgetRef.current;
    const broker = brokerRef.current;

    console.log('ASDASDASD', widget, datafeedAPI);

    if (!chartWidget || !datafeedAPI) {
      return;
    }

    try {
      chartWidget.resetCache();
      const chart = chartWidget.activeChart();
      const resolution = chart.resolution();
      const secInResolution = datafeedAPI.getSecFromResolution(resolution);
      chart.resetData();
      chart.setTimeFrame({
        res: resolution,
        val: {
          type: 'time-range' as TimeFrameType,
          from: currentDate - secInResolution * 60,
          to: currentDate + secInResolution * 10,
        } as TimeFrameValue,
      });

      if (broker) {
        broker.cleanData();
      }
    } catch (error) {
      console.warn('chart reset error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentDate changes on every playback tick; this reset should only run on explicit jumps (dateChangeCounter) or widget/datafeed changes, reading currentDate's latest value via closure
  }, [dateChangeCounter, isWidgetReady, datafeedAPI]);

  useEffect(() => {
    if (!datafeedAPI) return;

    datafeedAPI.clearBarStorage();
  }, [stepResolution, datafeedAPI]);

  return (
    <div className="h-full">
      <div id="tv_chart_container" />
      {hasLoadError && <LoadError />}
    </div>
  );
};
